import * as amplitude from "@amplitude/analytics-browser"
import type { BrowserClient, BrowserOptions } from "@amplitude/analytics-core"

import {
    AmplitudeEvent,
    COMMON_AMPLITUDE_CONFIG,
} from "@common-shared/config/amplitude.config.ts"
import type { IIndexable } from "@common-shared/types/general.types.ts"

import { sensitiveDataPlugin } from "../config/amplitude.config"
import { GENERAL_CONFIG } from "../config/general.config"
import { AuthHelper } from "./auth.helper"

let amplitudeClient: BrowserClient

export class AmplitudeHelper {
    /**
     * Get Instance
     */
    static getInstance(): BrowserClient | null {
        // Don't allow frontend Amplitude for demo sessions
        if (AuthHelper.isDemoSessionActive()) {
            return null
        }

        if (!amplitudeClient) {
            const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY

            // Check if API key is present
            if (!apiKey) {
                !GENERAL_CONFIG.isLocal &&
                    console.warn(
                        "Amplitude API key not found. Amplitude tracking disabled.",
                    )

                return null
            }

            try {
                amplitudeClient = amplitude.createInstance()
                amplitudeClient.add(sensitiveDataPlugin)

                const options: BrowserOptions = {
                    autocapture: false,
                    logLevel: 0,
                }

                amplitudeClient.init(apiKey, options)
            } catch (error) {
                console.error("Failed to initialize Amplitude:", error)
                return null
            }
        }

        amplitudeClient.setOptOut(false)

        return amplitudeClient
    }

    /**
     * Identify User
     */
    static identify(userId: string, properties: IIndexable = {}) {
        const instance = AmplitudeHelper.getInstance()

        if (!instance) {
            return
        }

        try {
            // Reset user to clear any previous data
            instance.reset()

            // Set user ID
            instance.setUserId(userId)

            // Apply user properties
            if (Object.keys(properties).length > 0) {
                const identify = new amplitude.Identify()

                Object.entries(properties).forEach(([key, value]) => {
                    identify.set(key, value)
                })

                instance.identify(identify)
            }
        } catch (error) {
            console.error("Error in identify:", error)
        }
    }

    /**
     * Log Event
     */
    static logEvent(
        event: AmplitudeEvent,
        properties: IIndexable,
        userId?: string,
    ) {
        const instance = AmplitudeHelper.getInstance()

        if (!instance) {
            return
        }

        // order of precedence matches backend
        const resolvedUserId = userId
            ? `${COMMON_AMPLITUDE_CONFIG.amplitudeUserIdPrefix}${userId}`
            : instance.getUserId()

        if (!resolvedUserId) {
            return
        }

        try {
            instance.logEvent(String(event), properties, {
                user_id: resolvedUserId,
                device_id: instance.getDeviceId(),
            })
        } catch (error) {
            console.error("Error logging event:", error)
        }
    }

    /**
     * Clear User
     */
    static clearUser() {
        const instance = AmplitudeHelper.getInstance()

        instance?.reset()

        instance?.setOptOut(true)
    }
}
